import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Radio,
  FormControlLabel,
  Chip,
  CircularProgress
} from '@mui/material';
import { 
  Download, 
  AccessTime, 
  CalendarToday, 
  DateRange,
  Star
} from '@mui/icons-material';
import tokens from '../../theme/tokens';
import { formatPrice } from '../../config/paymentProducts';

const PaymentOption = ({ 
  product, 
  productConfig, 
  isSelected, 
  onSelect, 
  translations,
  isPopular = false,
  isMobile = false,
  isProcessing = false
}) => {
  const handleSelect = () => {
    if (isMobile) {
      // On mobile, directly trigger checkout with this product
      onSelect(product.id);
    } else {
      // On desktop, just select the product
      onSelect(product.id);
    }
  };

  // Get icon based on product ID
  const getProductIcon = () => {
    const iconProps = { 
      fontSize: 20, 
      color: isSelected ? 'white' : tokens.colors.primary.main 
    };
    
    switch (product.id) {
      case 'pdf_one_time':
        return <Download sx={iconProps} />;
      case 'pdf_24h':
        return <AccessTime sx={iconProps} />;
      case 'pdf_week':
        return <CalendarToday sx={iconProps} />;
      case 'pdf_month':
        return <DateRange sx={iconProps} />;
      default:
        return <Download sx={iconProps} />;
    }
  };

  return (
    <Card
      sx={{
        cursor: isProcessing ? 'not-allowed' : 'pointer',
        border: isSelected 
          ? `3px solid ${tokens.colors.primary.main}` 
          : isPopular 
            ? `2px solid ${tokens.colors.primary.main}` 
            : `2px solid ${tokens.colors.borders.light}`,
        borderRadius: tokens.radius.lg,
        backgroundColor: isSelected 
          ? tokens.colors.primary.main 
          : tokens.colors.background.paper,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'visible',
        opacity: isProcessing ? 0.7 : 1,
        '&:hover': !isProcessing ? {
          borderColor: tokens.colors.primary.main,
          transform: 'translateY(-1px)',
          boxShadow: isSelected 
            ? '0 6px 20px rgba(0, 120, 255, 0.25)' 
            : '0 6px 20px rgba(0, 0, 0, 0.1)'
        } : {}
      }}
      onClick={isProcessing ? undefined : handleSelect}
    >
      {/* Popular Badge */}
      {isPopular && (
        <Chip
          icon={<Star sx={{ fontSize: 12, color: 'white' }} />}
          label={translations('pricing.mostPopular')}
          size="small"
          sx={{
            position: 'absolute',
            top: { xs: -8, sm: -10 },
            right: { xs: 12, sm: 16 },
            backgroundColor: tokens.colors.primary.main,
            color: 'white',
            fontWeight: tokens.typography.fontWeights.semibold,
            fontSize: { xs: '0.65rem', sm: '0.7rem' },
            height: { xs: 18, sm: 20 },
            '& .MuiChip-icon': {
              color: 'white'
            },
            zIndex: 1
          }}
        />
      )}

      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          minHeight: { xs: '48px', sm: '56px' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, sm: 0 }
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            flex: 1,
            width: { xs: '100%', sm: 'auto' }
          }}>
            <Box
              sx={{
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
                borderRadius: '50%',
                backgroundColor: isSelected 
                  ? 'rgba(255, 255, 255, 0.2)' 
                  : `${tokens.colors.primary.main}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: { xs: 1.5, sm: 2 },
                transition: 'all 0.3s ease',
                flexShrink: 0
              }}
            >
              {getProductIcon()}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: tokens.typography.fontWeights.bold,
                  color: isSelected ? 'white' : tokens.colors.text.primary,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  mb: { xs: 0.25, sm: 0.5 },
                  lineHeight: 1.2
                }}
              >
                {productConfig?.name || product.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: isSelected 
                    ? 'rgba(255, 255, 255, 0.8)' 
                    : tokens.colors.text.secondary,
                  fontSize: { xs: '0.8rem', sm: '0.85rem' },
                  lineHeight: 1.3
                }}
              >
                {productConfig?.description || product.description}
                {product.type === 'time_limited' && (
                  <Typography
                    component="span"
                    sx={{
                      display: 'block',
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      color: isSelected 
                        ? 'rgba(255, 255, 255, 0.6)' 
                        : tokens.colors.text.secondary,
                      fontStyle: 'italic',
                      mt: { xs: 0.25, sm: 0.5 },
                      opacity: 0.8
                    }}
                  >
                    {translations('payment.option.emailDelivery')}
                  </Typography>
                )}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            ml: { xs: 0, sm: 2 },
            width: { xs: '100%', sm: 'auto' },
            justifyContent: { xs: 'space-between', sm: 'flex-end' }
          }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: tokens.typography.fontWeights.bold,
                color: isSelected ? 'white' : tokens.colors.primary.main,
                mr: { xs: 1, sm: 2 },
                fontSize: { xs: '1.1rem', sm: '1.25rem' }
              }}
            >
              {formatPrice(product)}
            </Typography>
            
            {isMobile ? (
              // On mobile, show loading indicator when processing
              isProcessing && (
                <CircularProgress 
                  size={20} 
                  sx={{ 
                    color: isSelected ? 'white' : tokens.colors.primary.main 
                  }} 
                />
              )
            ) : (
              // On desktop, show regular radio button
              <FormControlLabel
                control={
                  <Radio
                    checked={isSelected}
                    onChange={handleSelect}
                    sx={{
                      color: isSelected 
                        ? 'rgba(255, 255, 255, 0.7)' 
                        : tokens.colors.primary.main,
                      '&.Mui-checked': {
                        color: isSelected ? 'white' : tokens.colors.primary.main
                      },
                      p: { xs: 0.5, sm: 1 }
                    }}
                  />
                }
                label=""
                sx={{ m: 0 }}
              />
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PaymentOption; 